import { useState } from 'react'
import { useCreateProduct, useUpdateProductPricing } from '@/hooks/use-api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { usePicklistOptions } from '@/hooks/use-picklists'
import type { Product } from '@/types'
import { explainError } from '@/lib/explain-error'

interface CreateProductModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface ProductFormData extends Omit<Partial<Product>, 'unit_price'> {
  unit_price?: string
  /**
   * Currency of the PRICE (a pricebook entry), which is a separate field from
   * the product record's own `currency_iso_code`. Tracked independently so the
   * two can never be silently conflated — see EditProductModal.
   */
  pricing_currency?: string
}

export function CreateProductModal({ open, onOpenChange }: CreateProductModalProps) {
  const { toast } = useToast()
  const createMutation = useCreateProduct()
  const updatePricingMutation = useUpdateProductPricing()
  const currencyCodes = usePicklistOptions('general', 'currency_code')

  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    product_code: '',
    description: '',
    is_active: true,
    renewal: false,
    family: '',
    currency_iso_code: 'USD',
    stock_keeping_unit: '',
    quantity_unit_of_measure: '',
    unit_price: '',
    pricing_currency: 'USD',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  // Once the user picks a price currency explicitly, stop mirroring the
  // product currency into it.
  const [pricingCurrencyTouched, setPricingCurrencyTouched] = useState(false)

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name?.trim()) {
      newErrors.name = 'Product name is required'
    }

    if (formData.unit_price && formData.unit_price.trim()) {
      const price = parseFloat(formData.unit_price)
      if (isNaN(price) || price < 0) {
        newErrors.unit_price = 'Unit price must be a valid non-negative number'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    try {
      // Pricing lives in sales.pricebook_entries, not on the product row.
      const { unit_price, pricing_currency, ...productData } = formData

      // Create the product
      const result = await createMutation.mutateAsync(productData)
      const createdProductId = result.id

      // If unit price was provided, create pricing entry
      if (unit_price && unit_price.trim()) {
        try {
          await updatePricingMutation.mutateAsync({
            id: createdProductId,
            unit_price: parseFloat(unit_price),
            // The price's own currency. Defaults to the product currency but is
            // tracked separately — they are different fields and conflating
            // them is what corrupted existing prices (see EditProductModal).
            currency_iso_code: pricing_currency || 'USD',
          })
          toast({
            title: 'Success',
            description: 'Product and pricing created successfully.',
          })
        } catch (pricingError) {
          toast({
            title: 'Partial Success',
            description: 'Product created, but failed to set pricing. You can add pricing later.',
            variant: 'default',
          })
        }
      } else {
        toast({
          title: 'Success',
          description: 'Product created successfully.',
        })
      }

      // Reset form and close modal
      setFormData({
        name: '',
        product_code: '',
        description: '',
        is_active: true,
        family: '',
        currency_iso_code: 'USD',
        stock_keeping_unit: '',
        quantity_unit_of_measure: '',
        unit_price: '',
        pricing_currency: 'USD',
      })
      setErrors({})
      onOpenChange(false)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to create product')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value }
      // On a NEW product there is no existing pricebook entry to disagree with,
      // so mirroring the product currency into the price currency is a safe and
      // helpful default — but only until the user sets the price currency
      // themselves, after which their choice wins.
      if (field === 'currency_iso_code' && !pricingCurrencyTouched) {
        next.pricing_currency = value
      }
      return next
    })
    if (field === 'pricing_currency') setPricingCurrencyTouched(true)
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby="create-product-description"
      >
        <DialogHeader>
          <DialogTitle>Create New Product</DialogTitle>
          <DialogDescription id="create-product-description">
            Fill in the details to create a new product. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Product Name */}
          <div>
            <Label htmlFor="name" className="required">
              Product Name *
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              aria-required="true"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'name-error' : undefined}
            />
            {errors.name && (
              <p id="name-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.name}
              </p>
            )}
          </div>

          {/* Product Code */}
          <div>
            <Label htmlFor="product_code">Product Code</Label>
            <Input
              id="product_code"
              value={formData.product_code}
              onChange={(e) => handleChange('product_code', e.target.value)}
            />
          </div>

          {/* Family */}
          <div>
            <Label htmlFor="family">Family</Label>
            <Input
              id="family"
              value={formData.family}
              onChange={(e) => handleChange('family', e.target.value)}
            />
          </div>

          {/* Currency */}
          <div>
            <Label htmlFor="currency_iso_code">Product Currency</Label>
            <Select
              value={formData.currency_iso_code}
              onValueChange={(value) => handleChange('currency_iso_code', value)}
            >
              <SelectTrigger id="currency_iso_code">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {currencyCodes.map((code) => (
                  <SelectItem key={code} value={code}>
                    {code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* SKU */}
          <div>
            <Label htmlFor="stock_keeping_unit">SKU</Label>
            <Input
              id="stock_keeping_unit"
              value={formData.stock_keeping_unit}
              onChange={(e) => handleChange('stock_keeping_unit', e.target.value)}
            />
          </div>

          {/* Unit of Measure */}
          <div>
            <Label htmlFor="quantity_unit_of_measure">Unit of Measure</Label>
            <Input
              id="quantity_unit_of_measure"
              value={formData.quantity_unit_of_measure}
              onChange={(e) => handleChange('quantity_unit_of_measure', e.target.value)}
              placeholder="e.g., Each, Box, Case"
            />
          </div>

          {/* Unit Price — creates a pricebook entry in the selected currency */}
          <div>
            <Label htmlFor="unit_price">Unit Price ({formData.pricing_currency}) (Optional)</Label>
            <div className="flex gap-2">
              <Input
                id="unit_price"
                type="number"
                step="0.01"
                min="0"
                value={formData.unit_price}
                onChange={(e) => handleChange('unit_price', e.target.value)}
                placeholder="0.00"
                aria-invalid={!!errors.unit_price}
                aria-describedby={errors.unit_price ? 'unit_price-error' : 'pricing_currency-help'}
              />
              <Select
                value={formData.pricing_currency}
                onValueChange={(value) => handleChange('pricing_currency', value)}
              >
                <SelectTrigger
                  id="pricing_currency"
                  className="w-28"
                  aria-label="Currency of the unit price"
                >
                  <SelectValue placeholder="Currency" />
                </SelectTrigger>
                <SelectContent>
                  {currencyCodes.map((code) => (
                    <SelectItem key={code} value={code}>
                      {code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p id="pricing_currency-help" className="text-xs text-muted-foreground mt-1">
              Creates a price list entry in this currency.
            </p>
            {errors.unit_price && (
              <p id="unit_price-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.unit_price}
              </p>
            )}
          </div>

          {/* Active and Renewal Switches */}
          <div className="flex items-center gap-6">
            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => handleChange('is_active', checked)}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="renewal"
                checked={formData.renewal}
                onCheckedChange={(checked) => handleChange('renewal', checked)}
              />
              <Label htmlFor="renewal">Renewal</Label>
            </div>
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={4}
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Product'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
